/**
 * TopicPillarService — read / update / delete for
 *   projects/{pid}/topic_pillars/{pillarId}
 *
 * Pillars are seeded by AuthorOnboardingHandler with `isDraft:true`.
 * Compatibility fields such as `weight` and `funnelStage` are still stored
 * for generation paths, but they are not exposed as primary user controls. This
 * service powers the user-facing Pillar editor where drafts are reviewed,
 * edited, and promoted.
 *
 * Firestore-direct, mirroring AuthorFirestoreService. All writes go through
 * stripUndefined (Sprint 16 bug A7) and use updateDoc with dot-notation
 * (feedback_firestore_merge_bug — never setDoc({merge:true}) for nested
 * partial updates).
 */

import {
  doc, setDoc, updateDoc, deleteDoc, serverTimestamp,
} from 'firebase/firestore';
import { CoreFirebaseService } from '@modules/firebase-core/services/CoreFirebaseService';
import { stripUndefined } from '../utils/stripUndefined';
import type { TopicPillarLite } from '../hooks/usePillars';

export type TopicPillarPatch = Partial<
  Pick<
    TopicPillarLite,
    | 'name'
    | 'description'
    | 'summary'
    | 'knowledgeContext'
    | 'keyTheses'
    | 'sourcePostIds'
    | 'sourceHighlights'
    | 'guardrails'
    | 'authorStance'
    | 'funnelStage'
    | 'weight'
    | 'isDraft'
  >
>;

export type TopicPillarCreateInput = {
  name: string;
  description?: string;
  summary?: string;
  knowledgeContext?: string;
  keyTheses?: TopicPillarLite['keyTheses'];
  sourcePostIds?: string[];
  sourceHighlights?: TopicPillarLite['sourceHighlights'];
  guardrails?: string[];
  authorStance?: string;
  funnelStage?: 'TOFU' | 'MOFU' | 'BOFU';
  weight?: number;
  authorIds?: string[];
};

export class TopicPillarService {
  private get db() {
    return CoreFirebaseService.getInstance().firestore;
  }

  private docRef(projectId: string, pillarId: string) {
    return doc(this.db, 'projects', projectId, 'topic_pillars', pillarId);
  }

  /**
   * Create a manually-authored pillar. Manual pillars start with
   * `isDraft: false` (already confirmed by virtue of being authored
   * directly by the user — they don't need a separate confirmation step).
   * Returns the new pillar id.
   */
  async create(
    projectId: string,
    input: TopicPillarCreateInput,
  ): Promise<string> {
    const pillarId = `pillar_${Date.now().toString(36)}_${Math.random()
      .toString(36)
      .slice(2, 8)}`;
    const ref = this.docRef(projectId, pillarId);
    const payload = stripUndefined({
      id: pillarId,
      projectId,
      name: input.name,
      description: input.description,
      summary: input.summary,
      knowledgeContext: input.knowledgeContext,
      keyTheses: input.keyTheses,
      sourcePostIds: input.sourcePostIds,
      sourceHighlights: input.sourceHighlights,
      guardrails: input.guardrails,
      authorStance: input.authorStance,
      funnelStage: input.funnelStage,
      weight: input.weight,
      authorIds: input.authorIds,
      isDraft: false,
      createdAt: serverTimestamp() as any,
      updatedAt: serverTimestamp() as any,
    }) as Record<string, unknown>;
    await setDoc(ref, payload);
    return pillarId;
  }

  /**
   * Patch a pillar. Always touches `updatedAt`. Pass `{ isDraft: false }`
   * to promote a draft pillar to confirmed status.
   */
  async update(
    projectId: string,
    pillarId: string,
    patch: TopicPillarPatch,
  ): Promise<void> {
    const ref = this.docRef(projectId, pillarId);
    const stripped = stripUndefined({
      ...patch,
      updatedAt: serverTimestamp() as any,
    }) as Record<string, unknown>;
    await updateDoc(ref, stripped);
  }

  /** Remove a pillar entirely. Caller is responsible for any topic-side cleanup. */
  async delete(projectId: string, pillarId: string): Promise<void> {
    await deleteDoc(this.docRef(projectId, pillarId));
  }

  /**
   * Confirm a single draft pillar in one shot — convenience wrapper around
   * update({ isDraft: false }).
   */
  async confirm(projectId: string, pillarId: string): Promise<void> {
    return this.update(projectId, pillarId, { isDraft: false });
  }
}
